import React, { useState, useEffect } from 'react';
import { api } from '../../../utils/apiClient';
import { Steps, Input, Select, Radio, DatePicker, Checkbox, Table, Button, Alert, Card } from 'antd';
import { SaveOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../../components/common';

const { Step } = Steps;
const { TextArea } = Input;

const ImpactAssessmentForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [sections, setSections] = useState([]);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    api.get('/impact-assessment/config')
      .then(response => {
        const fixedData = fixDuplicateQuestions(response.data);
        setSections(fixedData.sections);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching form configuration:', error);
        setError('Failed to load form configuration');
        setIsLoading(false);
      });

    if (id) {
      fetchAssessment(id);
    }
  }, [id]);

  const fetchAssessment = async (formId) => {
    try {
      const response = await api.get(`/impact-assessment/${formId}`);
      if (response.data && response.data.responses) {
        // Convert the responses array into the expected formData structure
        const formattedData = {};
        response.data.responses.forEach(item => {
          formattedData[item.section_id] = item.responses;
        });
        setFormData(formattedData);
      }

      // Set readOnly based on the URL
      const isViewMode = window.location.pathname.includes('/view/');
      setReadOnly(isViewMode);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      setError('Failed to load assessment data');
    }
  };

  const fixDuplicateQuestions = (data) => {
    const processQuestions = (questions) => {
      // Create a map to store unique questions and preserve order
      const questionMap = questions.reduce((acc, question) => {
        acc[question.label] = {
          ...question,
          order: question.id // Use id as order to maintain correct sequence
        };
        return acc;
      }, {});
  
      // Convert back to array and sort by id
      return Object.values(questionMap).sort((a, b) => a.id - b.id);
    };
  
    return {
      ...data,
      sections: data.sections.map(section => ({
        ...section,
        questions: section.questions.map(question => {
          if (question.type === 'group') {
            return {
              ...question,
              questions: processQuestions(question.questions || [])
            };
          }
          return question;
        })
      }))
    };
  };

  const handleInputChange = (sectionId, questionId, value) => {
    setFormData(prevData => ({
      ...prevData,
      [sectionId]: {
        ...prevData[sectionId],
        [questionId]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    // Check required fields
    for (const section of sections) {
      for (const question of section.questions) {
        if (question.required && (!formData[section.id] || !formData[section.id][question.label])) {
          toast.error('Required field', `${question.label} is required`);
          return;
        }
      }
    }

    try {
      const endpoint = id
        ? `/impact-assessment/update/${id}`
        : `/impact-assessment/submit`;

      const response = await api.post(endpoint, formData);
      console.log('Form submitted successfully:', response.data);
      navigate('/ai-ml/impact-assessment');
    } catch (error) {
      console.error('Error submitting form:', error);
      // Handle error (e.g., show an error message)
    }
  };

  const parseOptions = (options) => {
    if (typeof options === 'string') {
      try {
        return JSON.parse(options);
      } catch (error) {
        console.error('Error parsing options:', error);
        return [];
      }
    }
    return options || [];
  };

  const renderQuestion = (question, sectionId) => {
    if (!question) return null;  // Add this check

    const helpText = question.help_text ? (
      <div className="help-text" style={{ fontSize: '0.9em', color: '#888', marginBottom: '5px' }}>
        {question.help_text}
      </div>
    ) : null;

    const renderInput = (question, sectionId) => {
      if (!question || !question.type) return null;  // Add this check

      const disabledStyle = readOnly ? { backgroundColor: '#f5f5f5', color: '#888' } : {};

      switch (question.type) {
        case 'text':
          return (
            <Input
              placeholder={question.help_text}
              onChange={(e) => handleInputChange(sectionId, question.label, e.target.value)}
              value={formData[sectionId]?.[question.label] || ''}
              readOnly={readOnly}
              style={disabledStyle}
            />
          );
        case 'textarea':
          return (
            <TextArea
              placeholder={question.help_text}
              onChange={(e) => handleInputChange(sectionId, question.label, e.target.value)}
              value={formData[sectionId]?.[question.label] || ''}
              readOnly={readOnly}
              style={disabledStyle}
            />
          );
        case 'date':
          return (
            <DatePicker
              style={{ width: '100%', ...disabledStyle }}
              onChange={(_, dateString) => handleInputChange(sectionId, question.label, dateString)}
              value={formData[sectionId]?.[question.label] ? moment(formData[sectionId][question.label]) : null}
              disabled={readOnly}
            />
          );
        case 'radio':
          return (
            <Radio.Group
              onChange={(e) => handleInputChange(sectionId, question.label, e.target.value)}
              value={formData[sectionId]?.[question.label]}
              style={{ display: 'flex', flexDirection: 'column' }}
              disabled={readOnly}
            >
              {parseOptions(question.options).map((option, index) => (
                <Radio key={index} value={option.value || option} style={{ marginBottom: '8px' }}>
                  {option.label || option}
                </Radio>
              ))}
            </Radio.Group>
          );
        case 'select':
          return (
            <Select
              style={{ width: '100%', ...disabledStyle }}
              onChange={(value) => handleInputChange(sectionId, question.label, value)}
              value={formData[sectionId]?.[question.label]}
              disabled={readOnly}
            >
              {parseOptions(question.options).map((option, index) => (
                <Select.Option key={index} value={option.value || option}>
                  {option.label || option}
                </Select.Option>
              ))}
            </Select>
          );
        case 'checkbox':
          return (
            <Checkbox.Group
              options={parseOptions(question.options)}
              onChange={(checkedValues) => handleInputChange(sectionId, question.label, checkedValues)}
              value={formData[sectionId]?.[question.label] || []}
              disabled={readOnly}
            />
          );
        case 'table':
          return (
            <DynamicTable
              columns={parseOptions(question.columns)}
              formData={formData}
              setFormData={setFormData}
              sectionId={sectionId}
              questionId={question.label}
              readOnly={readOnly}
            />
          );
        default:
          return null;
      }
    };

  return (
    <div key={question.label} style={{ marginBottom: '20px' }}>
      <h4 style={{ fontSize: '1em', marginBottom: '5px' }}>
        {question.label}
        {question.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
      </h4>
      {helpText}
      {renderInput(question, sectionId)}
    </div>
  );
};

  const DynamicTable = ({ columns, formData, setFormData, sectionId, questionId, readOnly }) => {
    const [localDataSource, setLocalDataSource] = useState([]);
    const [editingKey, setEditingKey] = useState('');
  
    useEffect(() => {
      setLocalDataSource(formData[sectionId]?.[questionId] || []);
    }, [formData, sectionId, questionId]);
  
    // Add this function
    const isEditing = (record) => {
      return record.key === editingKey;
    };
  
    // Add this function
    const handleEdit = (key) => {
      setEditingKey(key);
    };
  
    const handleAdd = () => {
      const newKey = Date.now().toString();
      const newRow = {
        key: newKey,
        ...columns.reduce((acc, col) => ({ ...acc, [col.name]: '' }), {}),
      };
      setLocalDataSource([...localDataSource, newRow]);
      setEditingKey(newKey);
    };
  
    const handleDelete = (key) => {
      const newData = localDataSource.filter(item => item.key !== key);
      setLocalDataSource(newData);
      updateFormData(newData);
    };
  
    const handleSave = (key) => {
      const newData = [...localDataSource];
      const index = newData.findIndex(item => key === item.key);
      if (index > -1) {
        setEditingKey('');
        updateFormData(newData);
      }
    };
  
    const handleChange = (value, key, column) => {
      const newData = localDataSource.map(item =>
        item.key === key ? { ...item, [column]: value } : item
      );
      setLocalDataSource(newData);
    };
  
    const updateFormData = (newData) => {
      setFormData(prevFormData => ({
        ...prevFormData,
        [sectionId]: {
          ...prevFormData[sectionId],
          [questionId]: newData,
        },
      }));
    };
  
    const tableColumns = columns.map(col => ({
      title: col.name,
      dataIndex: col.name,
      key: col.name,
      render: (text, record) => {
        const editable = record.key === editingKey || record.isNew;
        
        // Handle different column types regardless of edit state
        switch (col.type) {
          case 'date':
            if (editable) {
              return (
                <DatePicker
                  value={text ? moment(text) : null}
                  onChange={(date, dateString) => handleChange(dateString, record.key, col.name)}
                  style={{ width: '100%' }}
                  disabled={readOnly}
                />
              );
            }
            // For non-editable date fields, display formatted date
            return text ? moment(text).format('YYYY-MM-DD') : '';
    
          case 'select':
            if (editable) {
              return (
                <Select
                  value={text}
                  onChange={(value) => handleChange(value, record.key, col.name)}
                  style={{ width: '100%' }}
                  disabled={readOnly}
                >
                  {(col.options || []).map((option, optionIndex) => (
                    <Select.Option key={`${col.name}-${optionIndex}`} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              );
            }
            // For non-editable select fields, display the selected value
            return text;
    
          default:
            if (editable) {
              return (
                <Input
                  value={text}
                  onChange={(e) => handleChange(e.target.value, record.key, col.name)}
                  disabled={readOnly}
                />
              );
            }
            return text;
        }
      },
    }));
  
    tableColumns.push({
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_, record) => {
        const editable = isEditing(record);
        return (
          <span style={{ display: 'flex', gap: '8px' }}>
            {editable ? (
              <Button
                size="small"
                icon={<SaveOutlined />}
                onClick={() => handleSave(record.key)}
                disabled={readOnly}
              >
                Save
              </Button>
            ) : (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record.key)}
                disabled={editingKey !== '' || readOnly}
              >
                Edit
              </Button>
            )}
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.key)}
              disabled={readOnly}
            >
              Delete
            </Button>
          </span>
        );
      },
    });
  
    return (
      <div>
        <Table
          dataSource={localDataSource}
          columns={tableColumns}
          pagination={false}
          rowKey="key"
          bordered
        />
        <div style={{ marginTop: '16px' }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={editingKey !== '' || readOnly}
            block
          >
            Add Row
          </Button>
        </div>
      </div>
    );
  };

  const renderSection = (section) => {
    if (!section || !section.questions) return null;  // Add this check

    return (
      <Card style={{ marginTop: '24px' }}>
        <h2 style={{ marginTop: 0 }}>{section.title}</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>{section.description}</p>
        {section.questions.map((question) => renderQuestion(question, section.id))}
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!sections || sections.length === 0) {
    return (
      <Alert
        message="No Sections Available"
        description="No sections available for this form"
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {readOnly && (
        <Alert
          message="View Mode - Read Only"
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}
      {!readOnly && id && (
        <Alert
          message="Edit Mode"
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Steps current={currentStep} style={{ marginBottom: '24px' }}>
        {sections.map((section, index) => (
          <Step key={index} title={section.title} />
        ))}
      </Steps>

      {sections[currentStep] && renderSection(sections[currentStep])}

      <div style={{
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px'
      }}>
        {currentStep > 0 && (
          <Button
            size="large"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            Previous
          </Button>
        )}
        {currentStep < sections.length - 1 ? (
          <Button
            type="primary"
            size="large"
            onClick={() => setCurrentStep(currentStep + 1)}
          >
            Next
          </Button>
        ) : (
          !readOnly && (
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
            >
              {id ? 'Update' : 'Submit'}
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default ImpactAssessmentForm;